import React, { Fragment, MouseEvent } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'

import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'

import Typography from '@material-ui/core/Typography'

import { Set } from 'immutable'

import { DefaultError, Dialog, Icon, Transition } from 'src/chrome'

import { CampaignFragment, getAllContacts } from 'src/graphql'
import { ContactSelector } from 'src/contacts/components'
import { CampaignLineCard } from 'src/campaigns/components'
import { PartialLine } from 'src/helpers/multitouch'

import styles from './saveAsModal.module.scss'
import { useMemo, useQueries, useState } from 'src/hooks'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { Text } from '@sendoutcards/quantum-design-ui'

interface Props {
  recipientIds: Set<string>
  existingCampaign?: Partial<CampaignFragment> | null
  lines: PartialLine[]
  onClose: () => void
  onSubmit: (result: {
    group: {
      name: string
      members: string[]
    }
    campaign: string
  }) => void
}

interface State {
  campaignName: string
  groupName: string
  context: 'SAVE_CAMPAIGN' | 'SAVE_GROUP'
  selected: Set<string>
  title?: string
}

const initialState: State = {
  campaignName: '',
  groupName: '',
  context: 'SAVE_CAMPAIGN',
  selected: Set(),
}

const Hooks = (props: Props) => {
  const { recipientIds: recipients, onSubmit } = props

  const [state, setState] = useState(initialState)

  const { campaignName, groupName, selected, context } = state

  const isEveryRecipientSelected = useMemo(
    () => selected.count() === recipients.count(),
    [selected, recipients],
  )

  const handleCampaignNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setState({ ...state, campaignName: event.currentTarget.value })
  }

  const handleGroupNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setState({ ...state, groupName: event.currentTarget.value })
  }

  const handleContextChange = () => {
    setState(state => ({
      ...state,
      context: context === 'SAVE_CAMPAIGN' ? 'SAVE_GROUP' : 'SAVE_CAMPAIGN',
    }))
  }

  const handleSelect = (id: string) => {
    setState(state => ({
      ...state,
      selected: selected.has(id) ? selected.delete(id) : selected.add(id),
    }))
  }

  const handleSelectAll = () => {
    setState(state => ({
      ...state,
      selected: isEveryRecipientSelected ? recipients : Set(),
    }))
  }

  const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    onSubmit({
      campaign: campaignName,
      group: {
        name: groupName,
        members: selected.toArray(),
      },
    })

    setState({ ...initialState })
  }

  return {
    state,
    recipients,
    isEveryRecipientSelected,
    handleCampaignNameChange,
    handleGroupNameChange,
    handleContextChange,
    handleSelect,
    handleSelectAll,
    handleSubmit,
  }
}

const SaveAsModal: React.FC<Props> = props => {
  const { existingCampaign, lines, onClose } = props

  const {
    state,
    recipients,
    isEveryRecipientSelected,
    handleCampaignNameChange,
    handleGroupNameChange,
    handleSelect,
    handleSelectAll,
    handleContextChange,
    handleSubmit,
  } = Hooks(props)

  const [allContacts] = useQueries(getAllContacts())

  const { campaignName, groupName, title, selected, context } = state

  const labelStyle: React.CSSProperties = {
    color: '#6F8394',
    fontWeight: 500,
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      style={{ transition: '300ms' }}
      scroll={'paper'}
    >
      <DialogTitle disableTypography={true}>
        <Typography variant="h6">
          {context === 'SAVE_CAMPAIGN'
            ? 'Save Cards & Gifts as Campaign'
            : 'Save your contacts as a Group'}
        </Typography>
        <span className={styles.close}>
          <Icon icon={'CLOSE'} color={'#6f8394'} size={18} onClick={onClose} />
        </span>
      </DialogTitle>
      <DialogContent className={styles.formContainer}>
        {context === 'SAVE_CAMPAIGN' && (
          <div className={styles.formInner}>
            <div className={styles.flex}>
              <div className={styles.field}>
                <TextField
                  name="campaignName"
                  label="Campaign Name"
                  InputLabelProps={{
                    style: labelStyle,
                  }}
                  InputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                  value={title}
                  onChange={handleCampaignNameChange}
                  className={styles.textField}
                />
              </div>
              <CampaignLineCard title="Order items" lines={lines} />
            </div>
            {!!existingCampaign && (
              <div className={styles.campaignOverlay}>
                <Text type="largeBody" alignment="center" inset="x2">
                  There is already a campaign associated with this order.
                </Text>
                <Text type="caption" alignment="center" weight="bold">
                  {existingCampaign.name}
                </Text>
                <Text
                  type="caption"
                  alignment="center"
                >{`Click next to continue and "Save Contacts"`}</Text>
              </div>
            )}
          </div>
        )}
        {context === 'SAVE_GROUP' && (
          <div className={styles.formInner}>
            <div className={`${styles.flex} ${styles.groupView}`}>
              <div className={styles.field}>
                <TextField
                  name="groupName"
                  label="Group Name"
                  InputLabelProps={{
                    style: labelStyle,
                  }}
                  InputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                  value={groupName}
                  onChange={handleGroupNameChange}
                  className={styles.textField}
                />
              </div>
              <Fragment>
                <Text type="caption">{`This order has ${recipients.count()} recipient${
                  recipients.count() === 1 ? '' : 's'
                }`}</Text>
                <ContactSelector
                  contacts={allContacts.filter(contact =>
                    recipients.contains(contact.id),
                  )}
                  selected={selected}
                  isEveryContactSelected={isEveryRecipientSelected}
                  onSelect={handleSelect}
                  onSelectAll={handleSelectAll}
                />
                <Text type="caption"> {`${selected.size} selected`}</Text>
              </Fragment>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {context !== 'SAVE_CAMPAIGN' && (
          <Button
            key={'Previous pane'}
            color={'secondary'}
            onClick={handleContextChange}
          >
            Back
          </Button>
        )}
        <div key={'Submit save'} id={'submit_saved_as_campaign'}>
          <Button
            key={'Submit save'}
            color={'primary'}
            disabled={campaignName === '' && groupName === ''}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
        {context === 'SAVE_CAMPAIGN' && (
          <Button
            key={'Next dialog pane'}
            color={'secondary'}
            onClick={handleContextChange}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default suspenseBoundary({
  component: SaveAsModal,
  unresolved: <Transition message="Loading..." />,
  failure: DefaultError,
})
